import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, MessageSquare, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { FEEDBACK_URL, NAV_GROUPS, isNavItemActive } from './navigation';

const itemClass = (active, collapsed) => [
  'flex items-center gap-2.5 h-8 rounded-control text-body w-full text-left',
  collapsed ? 'justify-center px-0' : 'px-2.5',
  active ? 'bg-signal-soft text-signal font-semibold' : 'text-ink hover:bg-hover',
].join(' ');

function NavItem({ item, collapsed, active, onAction }) {
  const Icon = item.icon;
  const content = (
    <>
      <Icon size={16} strokeWidth={active ? 2.25 : 1.75} className="shrink-0" aria-hidden="true" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </>
  );

  if (item.action) {
    return (
      <button type="button" className={itemClass(false, collapsed)} title={collapsed ? item.label : undefined} onClick={() => onAction(item.action)}>
        {content}
      </button>
    );
  }

  return (
    <Link to={item.to} className={itemClass(active, collapsed)} title={collapsed ? item.label : undefined} aria-current={active ? 'page' : undefined}>
      {content}
    </Link>
  );
}

export default function Sidebar({ collapsed, onToggleCollapsed, onAction }) {
  const { pathname } = useLocation();

  return (
    <aside
      className={`${collapsed ? 'w-sidebar-collapsed' : 'w-sidebar'} shrink-0 flex flex-col bg-sheet border-r border-rule`}
      aria-label="Main navigation"
    >
      <div className={`h-topbar shrink-0 flex items-center ${collapsed ? 'justify-center' : 'px-4'}`}>
        <Link to="/dashboard" className="text-section text-ink whitespace-nowrap overflow-hidden">
          {collapsed ? 'S' : 'SecOps'}
          {!collapsed && <span className="font-normal text-graphite"> SBOM</span>}
        </Link>
      </div>

      <nav className={`flex-1 overflow-y-auto pb-3 ${collapsed ? 'px-2' : 'px-2.5'}`}>
        {NAV_GROUPS.map((group) => (
          <div key={group.title} className="mt-3 first:mt-1">
            {collapsed ? (
              <div className="mx-2 mb-1.5 border-t border-rule" aria-hidden="true" />
            ) : (
              <h2 className="px-2.5 mb-1 text-small font-semibold text-graphite">{group.title}</h2>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <li key={item.label}>
                  <NavItem item={item} collapsed={collapsed} active={isNavItemActive(item, pathname)} onAction={onAction} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className={`shrink-0 border-t border-rule py-2 space-y-0.5 ${collapsed ? 'px-2' : 'px-2.5'}`}>
        <a href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer" className={itemClass(false, collapsed)} title={collapsed ? 'Feedback' : undefined}>
          <MessageSquare size={16} strokeWidth={1.75} className="shrink-0 text-graphite" aria-hidden="true" />
          {!collapsed && (
            <span className="flex items-center gap-1 text-graphite">
              Feedback
              <ArrowUpRight size={13} aria-hidden="true" />
            </span>
          )}
        </a>
        <button
          type="button"
          onClick={onToggleCollapsed}
          className={itemClass(false, collapsed)}
          title={collapsed ? 'Expand sidebar' : undefined}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <PanelLeftOpen size={16} strokeWidth={1.75} className="shrink-0 text-graphite" aria-hidden="true" />
          ) : (
            <>
              <PanelLeftClose size={16} strokeWidth={1.75} className="shrink-0 text-graphite" aria-hidden="true" />
              <span className="text-graphite">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
